import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Form, Button, Alert, Badge, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
}));

const ANumberCorrectionsModal = ({ show, onHide, anumber, onCorrectionDeleted }) => {
    const [corrections, setCorrections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'company_sample_id',
        direction: 'asc'
    });
    const [deleting, setDeleting] = useState(null);
    const [deletingAll, setDeletingAll] = useState(false);

    const fetchCorrections = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await GoldFinderApi.get(`/admin/soil-corrections/anumber/${anumber}`);
            setCorrections(response.data.corrections);
        } catch (error) {
            console.error('Error fetching corrections:', error);
            setError('Failed to load corrections for this ANumber');
        } finally {
            setLoading(false);
        }
    }, [anumber]);

    useEffect(() => {
        if (show && anumber) {
            fetchCorrections();
        }
    }, [show, anumber, fetchCorrections]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return '↕️';
        }
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString() + ' ' + 
               new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleDelete = async (correctionId, companySampleId) => {
        if (!window.confirm(`Are you sure you want to delete the correction for Company Sample ID "${companySampleId}"?`)) {
            return;
        }

        try {
            setDeleting(correctionId);
            await GoldFinderApi.delete(`/admin/soil-corrections/${correctionId}`);
            
            // Remove from local state
            setCorrections(prev => prev.filter(c => c.id !== correctionId));
            
            // Notify parent component
            if (onCorrectionDeleted) {
                onCorrectionDeleted();
            }
        } catch (error) {
            console.error('Error deleting correction:', error);
            alert('Failed to delete correction. Please try again.');
        } finally {
            setDeleting(null);
        }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm(
            `Are you sure you want to delete ALL ${corrections.length} corrections for ANumber ${anumber}?\n\n` +
            `This will:\n` +
            `• Remove all corrections from the corrections table\n` +
            `• Revert all affected samples to their original values\n` +
            `• Reapply any soil modifiers for this ANumber\n\n` +
            `This action cannot be undone.`
        )) {
            return;
        }

        try {
            setDeletingAll(true);
            const response = await GoldFinderApi.delete(`/admin/soil-corrections/anumber/${anumber}`);
            
            // Clear local state since all corrections are deleted
            setCorrections([]);
            
            // Notify parent component
            if (onCorrectionDeleted) {
                onCorrectionDeleted();
            }

            // Show success message
            alert(`Successfully deleted ${response.data.deleted_count} corrections for ANumber ${anumber}`);
            
        } catch (error) {
            console.error('Error deleting all corrections:', error);
            const errorMessage = error.response?.data?.error || 'Failed to delete all corrections. Please try again.';
            alert(errorMessage);
        } finally {
            setDeletingAll(false);
        }
    };

    const exportCorrections = () => {
        if (corrections.length === 0) return;

        const csvContent = [
            'ANumber,Company Sample ID,au ppb,Created By,Created Date,Updated Date',
            ...corrections.map(c => 
                `${c.anumber},"${c.company_sample_id}",${c.au_ppb},"${c.created_by}","${c.created_date}","${c.updated_date}"`
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `anumber_${anumber}_corrections.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const filteredCorrections = corrections.filter(correction =>
        !searchTerm || 
        correction.company_sample_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        correction.au_ppb.toString().includes(searchTerm) ||
        correction.created_by.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedCorrections = [...filteredCorrections].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Handle different data types
        if (sortConfig.key === 'au_ppb') {
            aValue = Number(aValue) || 0;
            bValue = Number(bValue) || 0;
        } else if (sortConfig.key === 'created_date' || sortConfig.key === 'updated_date') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const handleClose = () => {
        setSearchTerm('');
        setError(null);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Corrections for ANumber: <Badge bg="primary">{anumber}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger">
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading corrections...</p>
                    </div>
                ) : (
                    <>
                        <Row className="mb-3">
                            <Col md={8}>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faSearch} className="me-2 text-muted" />
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by Company Sample ID, au ppb, or created by..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={exportCorrections}
                                        disabled={corrections.length === 0}
                                    >
                                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                                        Export CSV
                                    </Button>
                                    <Button 
                                        variant="outline-danger" 
                                        onClick={handleDeleteAll}
                                        disabled={corrections.length === 0 || deletingAll}
                                        title="Delete all corrections for this ANumber"
                                    >
                                        {deletingAll ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm me-1" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
                                                Delete All
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Card>
                                    <Card.Body>
                                        <Row>
                                            <Col md={3}>
                                                <strong>Total Corrections:</strong><br />
                                                <Badge bg="primary" className="fs-6">{corrections.length}</Badge>
                                            </Col>
                                            <Col md={3}>
                                                <strong>Filtered Results:</strong><br />
                                                <Badge bg="secondary" className="fs-6">{filteredCorrections.length}</Badge>
                                            </Col>
                                            <Col md={3}>
                                                <strong>Date Range:</strong><br />
                                                {corrections.length > 0 ? (
                                                    <small>
                                                        {formatDate(Math.min(...corrections.map(c => new Date(c.created_date))))} - {formatDate(Math.max(...corrections.map(c => new Date(c.updated_date))))}
                                                    </small>
                                                ) : 'N/A'}
                                            </Col>
                                            <Col md={3}>
                                                <strong>Contributors:</strong><br />
                                                <Badge bg="info" className="fs-6">
                                                    {new Set(corrections.map(c => c.created_by)).size}
                                                </Badge>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {corrections.length === 0 ? (
                            <Alert variant="info">
                                No corrections found for this ANumber.
                            </Alert>
                        ) : (
                            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                <Table striped bordered hover>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                        <tr>
                                            <th 
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('company_sample_id')}
                                            >
                                                Company Sample ID {getSortIcon('company_sample_id')}
                                            </th>
                                            <th 
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('au_ppb')}
                                            >
                                                au ppb {getSortIcon('au_ppb')}
                                            </th>
                                            <th 
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('created_by')}
                                            >
                                                Created By {getSortIcon('created_by')}
                                            </th>
                                            <th 
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('created_date')}
                                            >
                                                Created Date {getSortIcon('created_date')}
                                            </th>
                                            <th 
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('updated_date')}
                                            >
                                                Updated Date {getSortIcon('updated_date')}
                                            </th>
                                            <th style={{ width: '80px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedCorrections.map((correction) => (
                                            <tr key={correction.id}>
                                                <td>
                                                    <strong>{correction.company_sample_id}</strong>
                                                </td>
                                                <td>
                                                    <Badge bg="success">{correction.au_ppb}</Badge>
                                                </td>
                                                <td>{correction.created_by}</td>
                                                <td>
                                                    <small>{formatDate(correction.created_date)}</small>
                                                </td>
                                                <td>
                                                    <small>{formatDate(correction.updated_date)}</small>
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(correction.id, correction.company_sample_id)}
                                                        disabled={deleting === correction.id}
                                                        title="Delete correction"
                                                    >
                                                        {deleting === correction.id ? (
                                                            <div className="spinner-border spinner-border-sm" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        ) : (
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ANumberCorrectionsModal;